/**
 * AMPD DCMS2 — Dropdown viewport clamp
 *
 * FrostUI positions dropdown menus with floating-ui, but wires up only the
 * `offset()` middleware — without `shift()`, so nothing keeps the popup inside
 * the viewport. A centered (`bottom`) or right-aligned (`bottom-end`) menu on a
 * trigger close to the left edge lands at a negative `left` and gets clipped.
 *
 * This module wraps `Dropdown.addComputePositionInTargetElement()` and watches
 * the menu for the `left`/`top` FrostUI writes, then nudges it horizontally so
 * it stays inside the viewport (pulled in by `VIEWPORT_MARGIN` when it does not).
 * Menus that already fit are left untouched — the correction is a no-op for
 * every layout that worked before, including the topbar menus that sit flush
 * against the right edge.
 *
 * The write is watched rather than awaited: FrostUI assigns the position from a
 * promise callback whose timing is not guaranteed (on a reopen it can land a
 * frame later than on the first open), so a `MutationObserver` on the inline
 * style is the only hook that catches every case.
 *
 * Menus carry `transition-all`, which means `getBoundingClientRect()` reports
 * the *animated* position while the menu slides into place. Measuring that and
 * writing a correction back feeds the animation its own output, so the geometry
 * here is derived from the final inline `left` plus the offset parent's box,
 * never from the menu's animated rect. `MAX_CORRECTIONS_PER_CYCLE` is a hard
 * stop in case a layout still manages to oscillate.
 *
 * Vertical placement is deliberately left alone: FrostUI's `top` is what drives
 * the open/close transition, and overflowing downwards scrolls rather than clips.
 */

/** Breathing room kept between the menu and the viewport edge, in pixels. */
const VIEWPORT_MARGIN = 8;

/** Class FrostUI puts on both the trigger and the menu element. */
const DROPDOWN_CLASS = 'fc-dropdown';

/** Upper bound on corrections per open/reposition — a safety net, not a budget. */
const MAX_CORRECTIONS_PER_CYCLE = 4;

/** Menus already watched for FrostUI position writes. */
const watchedTargets = new WeakSet();

/** Corrections applied since the current positioning cycle started. */
const correctionCounts = new WeakMap();

/**
 * Viewport offset of the box that `element`'s inline `left` is measured from.
 *
 * For an absolutely positioned element that is the padding box of its offset
 * parent; without an offset parent it is the viewport itself.
 *
 * @param {HTMLElement} element
 * @returns {number}
 */
function getContainingBoxLeft(element) {
    const parent = element.offsetParent;
    if (!(parent instanceof HTMLElement)) {
        return 0;
    }
    const parentStyle = window.getComputedStyle(parent);
    return parent.getBoundingClientRect().left + parseFloat(parentStyle.borderLeftWidth || '0');
}

/**
 * Shifts `element` horizontally so it fits inside the viewport.
 *
 * The correction is applied as a delta to the inline `left` FrostUI wrote, so
 * it works regardless of which offset parent the menu is positioned against.
 *
 * @param {HTMLElement} element
 * @returns {void}
 */
function clampToViewport(element) {
    if (!(element instanceof HTMLElement) || !element.isConnected) {
        return;
    }

    // Only touch elements FrostUI has actually positioned.
    const currentLeft = parseFloat(element.style.left);
    if (Number.isNaN(currentLeft)) {
        return;
    }
    if (window.getComputedStyle(element).position === 'static') {
        return;
    }

    const width = element.offsetWidth;
    if (width === 0) {
        return;
    }

    // Where the menu will sit once the transition settles — deliberately not
    // getBoundingClientRect(), which reports the in-flight animated position.
    const resolvedLeft = getContainingBoxLeft(element) + currentLeft;

    const viewportWidth = document.documentElement.clientWidth;

    // Only menus that genuinely leave the viewport are moved. The right edge is
    // tested against the viewport itself rather than against the margin, so a
    // menu merely sitting flush against the edge (topbar menus routinely do)
    // keeps the position it has always had.
    let desiredLeft = resolvedLeft;
    if (resolvedLeft + width > viewportWidth) {
        desiredLeft = viewportWidth - VIEWPORT_MARGIN - width;
    }
    // The left edge wins, so a menu wider than the viewport stays anchored
    // there instead of disappearing off the other side.
    if (desiredLeft < VIEWPORT_MARGIN) {
        desiredLeft = VIEWPORT_MARGIN;
    }

    const delta = desiredLeft - resolvedLeft;
    if (Math.abs(delta) < 0.5) {
        return;
    }

    const corrections = correctionCounts.get(element) ?? 0;
    if (corrections >= MAX_CORRECTIONS_PER_CYCLE) {
        return;
    }
    correctionCounts.set(element, corrections + 1);

    element.style.left = `${currentLeft + delta}px`;
}

/**
 * Clamps every dropdown menu that is currently open.
 *
 * @returns {void}
 */
function clampOpenDropdowns() {
    document.querySelectorAll(`.${DROPDOWN_CLASS}.open`).forEach((element) => {
        correctionCounts.set(element, 0);
        clampToViewport(element);
    });
}

/**
 * Starts clamping `target` whenever FrostUI rewrites its inline position.
 *
 * The observer stays attached for the element's lifetime — it is collected
 * together with the menu, and repositioning is the only thing that touches the
 * inline style of a dropdown menu.
 *
 * @param {HTMLElement} target
 * @returns {void}
 */
function watchTarget(target) {
    if (!(target instanceof HTMLElement) || watchedTargets.has(target)) {
        return;
    }
    watchedTargets.add(target);

    const observer = new MutationObserver(() => clampToViewport(target));
    observer.observe(target, { attributes: true, attributeFilter: ['style'] });
}

/**
 * Wraps the positioning method of a FrostUI Dropdown class.
 *
 * @param {*} frost Value assigned to `window.frost` by FrostUI.
 * @returns {void}
 */
function patchDropdownClass(frost) {
    const prototype = frost && frost.Dropdown && frost.Dropdown.prototype;
    if (!prototype || prototype.dcmsViewportClampInstalled) {
        return;
    }

    const originalCompute = prototype.addComputePositionInTargetElement;
    if (typeof originalCompute !== 'function') {
        return;
    }

    prototype.addComputePositionInTargetElement = function addComputePositionInTargetElement(...args) {
        const target = this._targetElement;
        if (target instanceof HTMLElement) {
            // Watch before positioning, so the write FrostUI is about to make
            // is already covered, and let this cycle correct again.
            correctionCounts.set(target, 0);
            watchTarget(target);
        }
        return originalCompute.apply(this, args);
    };

    prototype.dcmsViewportClampInstalled = true;
}

if (typeof window !== 'undefined') {
    patchDropdownClass(window.frost);

    // FrostUI can be present more than once (vendor bundle plus the footer
    // script tag); every copy reassigns `window.frost` with its own class, so
    // patch each assignment rather than just the one visible right now.
    let frostValue = window.frost;
    try {
        Object.defineProperty(window, 'frost', {
            configurable: true,
            get: () => frostValue,
            set: (value) => {
                frostValue = value;
                patchDropdownClass(value);
            },
        });
    } catch {
        // `window.frost` is not redefinable here — the direct patch above still
        // covers the copy that is already loaded.
    }

    window.addEventListener('resize', clampOpenDropdowns, { passive: true });
}

export { clampToViewport, clampOpenDropdowns };
